import { supabase } from './supabaseClient';
import { getPublicListingById } from './publicListings';

const PLACEHOLDER_IMAGE =
  'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=900&h=600&fit=crop&q=80';

const PROPERTY_SELECT = `
  id,
  name,
  location,
  price_per_month,
  room_count,
  occupancy_status,
  main_image_url,
  listing_description,
  bathroom_count,
  sqft,
  highlights,
  available_from,
  property_gallery_images ( image_url, sort_order )
`;

/** Accepts any RFC-style hex UUID string (avoids missing rows due to strict version checks). */
export function looksLikeUuid(id) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(id || '')
  );
}

function sortGalleryRows(rows) {
  return [...(rows || [])].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
  );
}

function galleryUrlsFromRow(row) {
  const rows = sortGalleryRows(row.property_gallery_images);
  const urls = rows.map((r) => r.image_url).filter(Boolean);
  if (urls.length > 0) return urls;
  if (row.main_image_url) return [row.main_image_url];
  return [PLACEHOLDER_IMAGE];
}

export function occupancyToTag(occupancy) {
  if (occupancy === 'high') return 'Featured';
  if (occupancy === 'medium') return 'New';
  return null;
}

function parseHighlights(value) {
  if (value == null) return [];
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string') {
    try {
      const j = JSON.parse(value);
      return Array.isArray(j) ? j.map(String).filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
}

const FALLBACK_DESCRIPTION =
  "Ask us for full details—viewings, bills, and what's included in the rent.";

export function mapPublicProperty(row) {
  const gallery = galleryUrlsFromRow(row);
  const image = gallery[0] || PLACEHOLDER_IMAGE;
  const beds = row.room_count != null ? Number(row.room_count) : 0;
  const desc = (row.listing_description || '').trim();
  const hl = parseHighlights(row.highlights);
  const avail = (row.available_from || '').trim();

  return {
    id: row.id,
    title: row.name || 'Untitled listing',
    location: row.location || '',
    beds,
    baths: row.bathroom_count != null ? Number(row.bathroom_count) : null,
    sqft: row.sqft != null ? Number(row.sqft) : null,
    rent: row.price_per_month != null ? Number(row.price_per_month) : 0,
    tag: occupancyToTag(row.occupancy_status),
    image,
    gallery,
    description: desc || FALLBACK_DESCRIPTION,
    highlights: hl.length > 0 ? hl : ['Enquire for full details and viewing slots'],
    availableFrom: avail || 'Contact for availability',
  };
}

export async function fetchPublicProperties() {
  if (!supabase) {
    return {
      data: [],
      error: new Error(
        'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.local.'
      ),
    };
  }

  const { data, error } = await supabase
    .from('properties')
    .select(PROPERTY_SELECT)
    .order('created_at', { ascending: false });

  if (error) return { data: [], error };
  return { data: (data || []).map(mapPublicProperty), error: null };
}

export async function fetchPublicPropertyById(id) {
  if (!id) return { data: null, error: null };

  const uuidLike = looksLikeUuid(id);

  if (uuidLike) {
    if (!supabase) {
      return {
        data: null,
        error: new Error(
          'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.local.'
        ),
      };
    }

    const { data, error } = await supabase
      .from('properties')
      .select(PROPERTY_SELECT)
      .eq('id', id)
      .maybeSingle();

    if (error) return { data: null, error };
    if (data) return { data: mapPublicProperty(data), error: null };
    return { data: null, error: new Error('Property not found.') };
  }

  const legacy = getPublicListingById(id);
  return { data: legacy, error: null };
}
